// src/pages/CustomerManagement.tsx
import React, { useState, useMemo, useCallback, useEffect } from 'react';
// Import useCustomerContext hook
import { useCustomerContext } from '../context/customerContext';
import { Customer } from '../utils/types';
import { useNavigate } from 'react-router-dom';
import { hasRole } from '../utils/auth';
import { fetchRolesByUserId } from '../utils/api';

const ITEMS_PER_PAGE = 12;

interface CustomerDetailModalProps {
    customer: Customer | null;
    onClose: () => void;
}

const CustomerDetailModal: React.FC<CustomerDetailModalProps> = ({ customer, onClose }) => {
    const [roles, setRoles] = useState<any[]>([]);
    const [loadingRoles, setLoadingRoles] = useState<boolean>(false);
    const [rolesError, setRolesError] = useState<string | null>(null);

    // --- Get the setter function from context ---
    const { setSelectedSessionCustomer } = useCustomerContext();

    useEffect(() => {
        if (customer?.user_id) {
            const fetchRoles = async () => {
                setLoadingRoles(true);
                setRolesError(null);
                setRoles([]);
                try {
                    const rolesData = await fetchRolesByUserId(customer.user_id);
                    // console.log('roles', rolesData); // Debugging

                    // --- Adjust based on your actual API response structure ---
                    // Assuming rolesData is like { user: {...}, roles: ["role_name"] }
                    if (rolesData && typeof rolesData === 'object' && rolesData.user && Array.isArray(rolesData.roles)) {
                        setRoles([rolesData]); // Store the whole object if needed
                    } else if (Array.isArray(rolesData)) {
                        // Handle if API returns just an array of role objects/strings
                        setRoles(rolesData);
                    } else {
                        setRoles([]); // Fallback for unexpected structure
                    }
                    // --- End structure assumption ---

                } catch (err: any) {
                    console.error(`Error fetching roles for user ${customer.user_id}:`, err);
                    setRolesError('Failed to fetch roles.');
                } finally {
                    setLoadingRoles(false);
                }
            };
            fetchRoles();
        } else {
            setRoles([]);
            setLoadingRoles(false);
            setRolesError(null);
        }
    }, [customer]);

    // --- Handler for selecting the customer ---
    const handleSelectClick = () => {
        if (customer) {
            let customerRole: string = 'dealer'; // Default role if fetch fails or no role found

            // Extract the role from the fetched roles state
            // Adjust this logic based on the actual structure of your 'roles' state
            if (roles.length > 0 && typeof roles[0] === 'object' && Array.isArray(roles[0].roles) && roles[0].roles.length > 0) {
                // Assuming roles state is like [{ user: {...}, roles: ["role_name"] }]
                customerRole = roles[0].roles[0]; // Get the first role
            } else if (roles.length > 0 && typeof roles[0] === 'string') {
                // Assuming roles state is just an array of role strings
                customerRole = roles[0];
            }
            // Add more conditions if your roles data structure is different

            // Create the customer object *with* the role included
            const customerToSelect: Customer = {
                ...customer,
                role: customerRole, // Add the determined role here
            };

            console.log("Selecting customer with role:", customerToSelect); // For debugging

            setSelectedSessionCustomer(customerToSelect); // Set the enriched customer object globally
            onClose(); // Close the modal
        }
    };

    if (!customer) return null;

    // Helper to render role info - adjust based on final roles state structure
    const renderRoleInfo = (roleData: any) => {
        // Assuming roleData is the object like { user: {...}, roles: ["role_name"] }
        if (typeof roleData === 'object' && roleData.user && Array.isArray(roleData.roles)) {
            return (
                <div className="flex justify-between items-center">
                    <div>
                        <span>UID: {roleData.user.id}</span>
                        &nbsp;-&nbsp;
                        <span>{roleData.user.name?.toUpperCase()}</span>
                        &nbsp;-&nbsp;
                        <span className='text-pink-700 font-medium'>{roleData.roles[0]?.toUpperCase() || 'N/A'}</span>
                    </div>
                    {/* --- Make "Select" clickable --- */}
                    <button
                        onClick={handleSelectClick}
                        className="text-green-600 hover:text-green-800 font-semibold cursor-pointer underline ml-4" // Added margin
                    >
                        Select
                    </button>
                </div>
            );
        }
        // Add fallback rendering if roles state holds just strings or other structures
        return <span className="text-gray-500">Role data format unclear.</span>;
    };


    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
            <div className="bg-white p-6 rounded-lg shadow-xl max-w-md w-full">
                <h2 className="text-2xl font-bold mb-4">Customer Details</h2>
                <div className="space-y-2 text-sm">
                    <p><strong>ID:</strong> {customer.id}</p>
                    <p><strong>Name:</strong> {customer.name || 'N/A'}</p>
                    <p><strong>Email:</strong> {customer.email || 'N/A'}</p>
                    <p><strong>Phone:</strong> {customer.phone || 'N/A'}</p>
                    <p><strong>City:</strong> {customer.city || 'N/A'}</p>

                    <div className="mt-3 pt-3 border-t">
                        <p className="font-semibold mb-1">User Info:</p>
                        {loadingRoles && <p className="text-gray-500">Loading user roles...</p>}
                        {rolesError && <p className="text-red-500">{rolesError}</p>}
                        {!loadingRoles && !rolesError && (
                            roles.length > 0
                                ? roles.map((roleInfo, index) => <div key={index}>{renderRoleInfo(roleInfo)}</div>)
                                : <span className="text-gray-500">No user/role info found.</span>
                        )}
                    </div>
                </div>
                
                <button
                    onClick={onClose}
                    className="mt-6 bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded w-full"
                >
                    Close
                </button>
            </div>
        </div>
    );
};


// --- CustomerManagement Component (No changes needed below this line for this feature) ---
const CustomerManagement = () => {
    // ... (rest of the component remains the same) ...

    const navigate = useNavigate();

    useEffect(() => {
        if (!hasRole(['super_admin', 'staff'])) {
            navigate('/');
        }
    }, [navigate]);


    const { customers, loading, error, refreshCustomers } = useCustomerContext();
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);


    // --- Filtering Logic ---
    const filteredCustomers = useMemo(() => {
        if (!searchTerm) {
            return customers;
        }
        const lowerCaseSearchTerm = searchTerm.toLowerCase();
        return customers.filter(customer =>
            customer.name?.toLowerCase().includes(lowerCaseSearchTerm) ||
            customer.email?.toLowerCase().includes(lowerCaseSearchTerm) ||
            customer.phone?.toLowerCase().includes(lowerCaseSearchTerm) ||
            customer.city?.toLowerCase().includes(lowerCaseSearchTerm) ||
            customer.id?.toString().includes(lowerCaseSearchTerm)
        );
    }, [customers, searchTerm]);

    // --- Pagination Logic ---
    const totalPages = Math.ceil(filteredCustomers.length / ITEMS_PER_PAGE);

    const currentCustomers = useMemo(() => {
        const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
        const endIndex = startIndex + ITEMS_PER_PAGE;
        return filteredCustomers.slice(startIndex, endIndex);
    }, [filteredCustomers, currentPage]);

    // --- Event Handlers ---
    const handleSearchChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(event.target.value);
        setCurrentPage(1);
    }, []);

    const handlePageChange = useCallback((page: number) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
        }
    }, [totalPages]);

    const handleRefresh = useCallback(() => {
        refreshCustomers();
        setSearchTerm('');
        setCurrentPage(1);
    }, [refreshCustomers]);

    // --- Modal Handlers ---
    const handleSelectCustomer = useCallback((customer: Customer) => {
        setSelectedCustomer(customer);
        setIsModalOpen(true);
    }, []);

    const handleCloseModal = useCallback(() => {
        setIsModalOpen(false);
        setSelectedCustomer(null);
    }, []);


    // --- Render Logic ---
    if (loading) return <div className="container mx-auto p-4 text-center">Loading customers...</div>;
    if (error) return <div className="container mx-auto p-4 text-red-500">Error: {error}</div>;

    return (
        <div className="container mx-auto p-4">
            <h1 className="text-3xl font-bold mb-6">Customer Management</h1>

            {/* Search and Refresh Controls */}
            <div className="sticky top-12 left-2 right-2 z-10 border border-gray-400 rounded shadow-md bg-white p-1 flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
                <input
                    type="text"
                    placeholder="Search by name, email, phone, city, or ID..."
                    value={searchTerm}
                    onChange={handleSearchChange}
                    className="p-2 border rounded w-full sm:w-1/2 lg:w-1/3 focus:outline-none focus:ring focus:border-blue-300"
                />
                <button
                    onClick={handleRefresh}
                    className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded transition duration-200 w-full sm:w-auto"
                >
                    Refresh List
                </button>
            </div>

            {/* Customer List */}
            {filteredCustomers.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {currentCustomers.map((customer) => (
                        <div key={customer.id} className="bg-white p-4 rounded-lg shadow border border-gray-200 flex flex-col justify-between">
                            <div className="mb-3">
                                <h4 className="font-semibold text-lmdg mb-1 truncate" title={customer.name || ''}>
                                    {customer.name || 'N/A'}
                                </h4>
                                <p className="text-sm text-gray-600 truncate" title={customer.email || ''}>
                                    <span className="font-medium">Email:</span> {customer.email || 'N/A'}
                                </p>
                                <p className="text-sm text-gray-600">
                                    <span className="font-medium">Phone:</span> {customer.phone || 'N/A'}
                                </p>
                                <p className="text-sm text-gray-600">
                                    <span className="font-medium">City:</span> {customer.city || 'N/A'}
                                </p>
                                {/* <p className="text-sm  text-gray-500 mt-1">
                                    <span className="font-medium">CID:</span> {customer.id}
                                </p>
                                <p className="text-sm  text-gray-500 mt-1">
                                    <span className="font-medium">UID:</span> {customer.user_id}
                                </p> */}
                            </div>
                            <div className='flex flex-row gap-2'>
                            <button
                                onClick={() => handleSelectCustomer(customer)}
                                className="w-full bg-indigo-500 hover:bg-indigo-700 text-white text-sm font-bold py-2 px-4 rounded transition duration-200 mt-2"
                            >
                                View Details
                            </button>
                            {/* <button
                                onClick={() => handleSelectCustomer(customer)}
                                className="w-full bg-indigo-500 hover:bg-indigo-700 text-white text-sm font-bold py-2 px-4 rounded transition duration-200 mt-2"
                            >
                                View Details
                            </button> */}
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <p className="text-center text-gray-500 mt-6">
                    {searchTerm ? 'No customers found matching your search.' : 'No customers available.'}
                </p>
            )}

            {/* Pagination Controls */}
            {totalPages > 1 && (
                <div className="flex justify-center items-center mt-8 space-x-2">
                    <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="px-4 py-2 border rounded bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        Previous
                    </button>
                    <span className="text-sm text-gray-700">
                        Page {currentPage} of {totalPages}
                    </span>
                    <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className="px-4 py-2 border rounded bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        Next
                    </button>
                </div>
            )}

            {/* Render the Modal */}
            {isModalOpen && (
                <CustomerDetailModal
                    customer={selectedCustomer}
                    onClose={handleCloseModal}
                />
            )}
        </div>
    );
};

export default CustomerManagement;
